import { Injectable, EventEmitter } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { options, store, presets, Level } from './interfaces/input-interfaces';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from "@environments/environment";
import { Prompt } from './components/classes/prompt-class';
import * as PROMPTS from "projects/estimate/src/app/data/prompt.constant";
import { MatTableDataSource } from '@angular/material/table';
import { Phase, Phases } from './interfaces/output-interfaces';

@Injectable({
    providedIn: 'root',
})
export class EstimateService {
    constructor(private http: HttpClient) { }

    // Admin data
    private chatUrl = environment.backendBaseURL + "/conversation/ask_genai";
    private subscriptionId: string;
    public autoPilot: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    // Output States
    public disablePage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    public tabChange: BehaviorSubject<number> = new BehaviorSubject<number>(undefined)

    // Tab 1 Data
    public selectedProjectType: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public projectHeadline: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public projectClassification: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);
    public timeFrameEstimate: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);

    // Tab 2 Data
    public summary: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public selectedTimeFrame: BehaviorSubject<string> = new BehaviorSubject<string>('');
    public timeSensitiveDetails: BehaviorSubject<Array<string>> = new BehaviorSubject<Array<string>>([]);

    // Tab 3 Data
    public initTable: Subject<void> = new Subject<void>();
    public prepareOutputTable: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    public originalPhaseArray: Phases = { phases: [] };
    public displayedPhaseArray: Phases = { phases: [] };
    public timeUnit: string = 'month';
    public dataSource: MatTableDataSource<any>;

    // Dialogue
    public dialogueInput: Subject<string> = new Subject<string>();

    // Tab 4 Data
    public disableGantChart: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    // Prompt and Result Stores
    public prompts: Array<Prompt> = [];

    // Initialize the store with default values using BehaviorSubject.
    // Rewrite as array of prompts
    private initialValue: store = {
        stepZero: {
            status: 'init',
        },
        stepOne: {
            status: 'init',
        },
        stepTwo: {
            status: 'init',
        },
        stepThree: {
            status: 'init',
        },
        stepFour: {
            status: 'init',
        },
        stepFive: {
            status: 'init',
        },
        addPhase: {
            status: 'init',
        }
    }
    public store: BehaviorSubject<store> = new BehaviorSubject(
        JSON.parse(JSON.stringify(this.initialValue))
    );

    // Prompt functions
    addPrompt(id: string): void {
        this.disableGantChart.next(true);
        const currentPhase = this.getPhaseById(id);
        const nextPhase = this.getPhaseById(String(Number(id) + 1));
        this.createPrompt(
            'addPhase',
            PROMPTS.addPhase,
            {
                "PHASE-ID": String(Number(id) + 1),
                "TOPIC": this.projectHeadline.getValue(),
                "PREVIOUS": JSON.stringify(currentPhase),
                "NEXT": JSON.stringify(nextPhase),
                "TIME-FRAME": currentPhase.phaseTime
            },
            'text-bison',
            true,
            'crazy'
        );
    }

    replacePrompt(id: string, currentPhase: Phase, instruction?: string): void {
        this.disableGantChart.next(true);
        const previousPhase = this.getPhaseById(String(Number(id) - 1));
        const nextPhase = this.getPhaseById(String(Number(id) + 1));
        this.createPrompt(
            'addPhase',
            PROMPTS.replacePhase,
            {
                "PHASE-ID": id,
                "TOPIC": currentPhase.phaseDetails + instruction,
                "PREVIOUS": JSON.stringify(previousPhase),
                "NEXT": JSON.stringify(nextPhase),
                "TIME-FRAME": currentPhase.phaseTime
            },
            'text-bison',
            true,
            'crazy'
        );
    }

    dependencyPrompt(): void {
        this.disableGantChart.next(true);
        if (this.displayedPhaseArray.phases.length <= 4) {
            this.createPrompt(
                'stepFive',
                PROMPTS.chartPromptInstruction,
                {
                    "MILESTONES": this.milestoneSummaryPrompt()
                },
                'text-bison',
                true,
                'relaxed',
                undefined,
                false
            );
        }
    }

    milestoneSummaryPrompt(): string {
        let milestonesString = "";

        this.displayedPhaseArray.phases.forEach((phase) => {
            phase.phaseMilestones.forEach((milestone) => {
                let milestoneString = `{
        "ID": "${milestone.milestoneId}",
        "Title": "${milestone.milestoneTitle}",
        "Time": "${milestone.milestoneTime}",
        "SimultaneousIDs": "<comma_separated_list_of_IDs>",
        "DependendIDs": "<comma_separated_list_of_IDs>"
      }`;
                milestonesString += milestoneString + ',';
            })
        });

        // Removing the trailing comma
        return milestonesString.slice(0, -1);
    };

    setSubscriptionId(subscriptionId: string): void {
        this.subscriptionId = subscriptionId;
    }

    enablePage(disable: boolean): void {
        this.disablePage.next(!disable)
    }

    setProgress(options: options): void {
        const { level, status, response, prompt, feedback, display } = options;
        const store: store = { ...this.store.value };

        store[level].status = status;
        if (status === 'done' || status === 'afterOutput') store[level].display = true;
        if (status === 'init' || status === 'afterInput') store[level].display = false;
        if (response) store[level].response = response;
        if (prompt) store[level].prompt = prompt;
        if (feedback) store[level].feedback = feedback;
        if (display) store[level].display = display;

        this.store.next(store);
    }

    resetLevel(options: options): void {
        const { level, status } = options;
        const store: store = { ...this.store.value };

        store[level].status = status;

        store[level].response = undefined;
        store[level].prompt = undefined;
        store[level].feedback = undefined;
        store[level].display = undefined;

        this.store.next(store);
    }

    resetProgress(): void {

        // Admin data
        this.autoPilot = new BehaviorSubject<boolean>(false);

        // Output States
        this.disablePage = new BehaviorSubject<boolean>(true);
        this.tabChange = new BehaviorSubject<number>(undefined)

        // Tab 1 Data
        this.selectedProjectType = new BehaviorSubject<string>('');
        this.projectHeadline = new BehaviorSubject<string>('');
        this.projectClassification = new BehaviorSubject<Array<string>>([]);
        this.timeFrameEstimate = new BehaviorSubject<Array<string>>([]);

        // Tab 2 Data
        this.summary = new BehaviorSubject<string>('');
        this.selectedTimeFrame = new BehaviorSubject<string>('');
        this.timeSensitiveDetails = new BehaviorSubject<Array<string>>([]);

        // Tab 3 Data
        this.initTable = new Subject<void>();
        this.prepareOutputTable = new BehaviorSubject<any>(undefined);
        this.originalPhaseArray = { phases: [] };
        this.displayedPhaseArray = { phases: [] };
        this.timeUnit = 'month';

        // Dialogue
        this.dialogueInput = new Subject<string>();

        // Tab 4 Data
        this.disableGantChart = new BehaviorSubject<boolean>(true);

        // Prompt and Result Stores
        this.prompts = [];

        // Initialize the store with default values using BehaviorSubject.
        // Rewrite as array of prompts

        this.store = new BehaviorSubject(
            JSON.parse(JSON.stringify(this.initialValue))
        );
    }

    resetAfterTimeFrame(): void {
        this.selectedTimeFrame = new BehaviorSubject<string>('');
        this.originalPhaseArray = { phases: [] };
        this.displayedPhaseArray = { phases: [] };
        this.prompts.splice(3);
        this.resetLevel({
            level: 'stepThree',
            status: 'init'
        })
        this.resetLevel({
            level: 'stepFour',
            status: 'init'
        })
        this.resetLevel({
            level: 'stepFive',
            status: 'init'
        })
        this.resetLevel({
            level: 'addPhase',
            status: 'init'
        })
    }

    resetAfterClassification(): void {
        this.resetAfterTimeFrame();
        this.selectedProjectType = new BehaviorSubject<string>('');
        this.prompts.splice(2);
        this.resetLevel({
            level: 'stepTwo',
            status: 'init'
        })
    }

    extractTimeUnit(inputString: string, setGlobal?: boolean): string {
        var timeUnit: string;
        if (inputString.includes("year")) timeUnit = "year";
        if (inputString.includes("month")) timeUnit = "month";
        if (inputString.includes("week")) timeUnit = "week";
        if (inputString.includes("day")) timeUnit = "day";
        if (inputString.includes("hour")) timeUnit = "hour";
        if (setGlobal) this.timeUnit = timeUnit
        return timeUnit
    }

    extractTime(timeString: string): { integer: number, unit: string } {
        return {
            integer: Number(timeString.match(/\d+/)[0]),
            unit: String(timeString.match(/\b[^0-9\s]+\b/g))
        };
    }

    convertToDays(number, unit) {
        unit = unit.toLowerCase();
        switch (unit) {
            case 'hour':
            case 'hours':
                return number / 24;
            case 'day':
            case 'days':
                return number;
            case 'week':
            case 'weeks':
                return number * 7;
            case 'month':
            case 'months':
                return number * 30;
            case 'year':
            case 'years':
                return number * 365;
            default:
                console.error('Invalid unit provided', unit);
                return 0
        }
    }

    getPhaseById(id: string): Phase | undefined {
        return this.displayedPhaseArray.phases.find(phase => phase.phaseId === id);
    }

    // API
    askModel(prompt: string, model: string, preset = 'strict'): Observable<any> | undefined {
        if (model === 'text-bison') return this.askTextBison(prompt, "You are a helpful assistant", presets[preset]);
        if (model === 'chat-bison') return this.askChatBison(prompt);
        if (model === 'code-bison') return this.askCodeBison(prompt);
        return undefined
    }

    askCodeBison(query: string, context: string = "You are a helpful assistant", examples: Array<string> = []): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                "subscription-id": this.subscriptionId,
                "Content-Type": "application/json"
            }),
        };
        var message = {
            "message": query,
            "palm_params": {
                "model": "code-bison",
                "context": context,
                "parameters": {
                    "max_output_tokens": 800,
                    "temperature": 0.1
                }
            }
        }
        return this.http.post<any>(
            `${this.chatUrl}`,
            message,
            httpOptions
        );
    }

    askTextBison(query: string, context: string = "You are a helpful assistant", params = presets.strict): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                "subscription-id": this.subscriptionId,
                "Content-Type": "application/json"
            }),
        };
        var message = {
            "message": query,
            "palm_params": {
                "model": "text-bison",
                "context": context,
                "parameters": params,
                // TODO try to put up-to-date summary into the history to keep context
                "history": []
            }
        }
        return this.http.post<any>(
            `${this.chatUrl}`,
            message,
            httpOptions
        );
    }

    askChatBison(query: string, context: string = "You are a helpful assistant", examples: any = []): Observable<any> {
        const httpOptions = {
            headers: new HttpHeaders({
                "subscription-id": this.subscriptionId,
                "Content-Type": "application/json"
            }),
        };
        var message = {
            "message": query,
            "palm_params": {
                "model": "chat-bison",
                "context": context,
                "parameters": {
                    "max_output_tokens": 800,
                    "temperature": 0.1,
                    "top_p": 0.95,
                    "top_k": 40
                },
                "history": [],
                "examples": examples
            }
        }
        return this.http.post<any>(
            `${this.chatUrl}`,
            message,
            httpOptions
        );
    }

    // Prompt
    createPrompt(
        step: Level,
        prompt: string,
        data: any,
        model?: string,
        jsonResponse?: boolean,
        paramOption?: string,
        eventEmitModel?: EventEmitter<any>,
        enableLoading?: boolean,
        refinementPhase = false,
        refinementMilestone = false
    ): void {
        this.prompts.push(new Prompt(
            this, step, prompt, data, model, jsonResponse, paramOption, eventEmitModel, enableLoading, refinementPhase, refinementMilestone
        ));
    }

    // Helper
    formatTime(time: number): string {
        const timeUnit = this.timeUnit;
        return time > 1 ? `${time} ${timeUnit}s` : `${time} ${timeUnit}`;
    }

    cleanJsonString(jsonString: string): string {
        const toReplace = new RegExp('`', 'g');
        var cleanedJsonString = jsonString.split(';')[0].replace(toReplace, "").trim();
        cleanedJsonString = cleanedJsonString.replace(/,\s*([}\]])/g, '$1');
        return this.stripJSON(cleanedJsonString);
    }

    stripJSON(jsonString: string): string {
        const startIndex = jsonString.indexOf('{');
        const endIndex = jsonString.lastIndexOf('}');

        if (startIndex === -1 || endIndex === -1) {
            console.error('Invalid JSON string - returning empty string');
            return '';
        }

        return jsonString.substring(startIndex, endIndex + 1);
    }

    validateJsonResponse(response: string): string {
        const cleanString = this.cleanJsonString(response);
        const testOutput = JSON.parse(cleanString);
        return cleanString;
    }
}
